import React from "react";

export interface FilterState {
  categories: string[];
  minScore: number;
  maxScore: number;
  hideDefaults: boolean;
}

interface EventFiltersProps {
  value: FilterState;
  onChange: (value: FilterState) => void;
}

interface CategoryChip {
  key: string;
  label: string;
  emoji: string;
}

interface ScoreTier {
  key: string;
  label: string;
  min: number;
  max: number;
  segmentColor: string;
  dotColor: string;
}

const CATEGORIES: CategoryChip[] = [
  { key: "hasici", label: "Hasiči", emoji: "🔥" },
  { key: "sport", label: "Sport", emoji: "⚽" },
  { key: "kultura", label: "Kultura", emoji: "🎵" },
  { key: "festival", label: "Festival", emoji: "🎪" },
  { key: "trhy", label: "Trhy", emoji: "🛒" },
  { key: "charity", label: "Charity", emoji: "❤️" },
  { key: "prednaska", label: "Přednáška", emoji: "📚" },
  { key: "divadlo", label: "Divadlo", emoji: "🎭" },
  { key: "kino", label: "Kino", emoji: "🎬" },
  { key: "other", label: "Ostatní", emoji: "📅" },
];

const SCORE_TIERS: ScoreTier[] = [
  { key: "all", label: "Vše", min: 1, max: 10, segmentColor: "#a1a1a1", dotColor: "#a1a1a1" },
  { key: "low", label: "Nízké", min: 1, max: 3, segmentColor: "#a1a1a1", dotColor: "#666666" },
  { key: "mid", label: "Střední", min: 4, max: 7, segmentColor: "#facc15", dotColor: "#facc15" },
  { key: "top", label: "Top", min: 8, max: 10, segmentColor: "#4ade80", dotColor: "#4ade80" },
];

function getActiveTierKey(min: number, max: number): string {
  for (const tier of SCORE_TIERS) {
    if (tier.min === min && tier.max === max) return tier.key;
  }
  return "";
}

export const EventFilters: React.FC<EventFiltersProps> = ({ value, onChange }) => {
  const toggleCategory = (key: string) => {
    const current = value.categories;
    const updated = current.includes(key)
      ? current.filter((c) => c !== key)
      : [...current, key];
    onChange({ ...value, categories: updated });
  };

  const selectTier = (tier: ScoreTier) => {
    onChange({ ...value, minScore: tier.min, maxScore: tier.max });
  };

  const toggleShowAll = () => {
    onChange({ ...value, hideDefaults: !value.hideDefaults });
  };

  const activeTierKey = getActiveTierKey(value.minScore, value.maxScore);
  const activeTier = SCORE_TIERS.find((t) => t.key === activeTierKey) ?? null;

  return (
    <div
      className="service-card"
      style={{
        borderRadius: "14px",
        padding: "1.25rem",
        display: "flex",
        flexDirection: "column",
        gap: "1.25rem",
      }}
    >
      {/* ——— Category chips ——— */}
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.625rem" }}>
          <span className="eyebrow-label">Kategorie</span>
          {value.categories.length > 0 && (
            <button
              onClick={() => onChange({ ...value, categories: [] })}
              style={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 300,
                fontSize: "0.6875rem",
                letterSpacing: "0.04em",
                color: "#a1a1a1",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                transition: "color 0.15s ease",
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#fafafa"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#a1a1a1"; }}
            >
              Zrušit výběr
            </button>
          )}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.375rem" }}>
          {CATEGORIES.map((cat) => {
            const isActive = value.categories.includes(cat.key);
            return (
              <button
                key={cat.key}
                onClick={() => toggleCategory(cat.key)}
                aria-pressed={isActive}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.3rem",
                  padding: "0.3125rem 0.75rem",
                  borderRadius: "20px",
                  border: isActive
                    ? "1px solid rgba(255,255,255,0.4)"
                    : "1px solid rgba(255,255,255,0.15)",
                  background: isActive ? "#ffffff" : "transparent",
                  color: isActive ? "#050505" : "#a1a1a1",
                  fontFamily: isActive ? "'Syne', sans-serif" : "'Inter', sans-serif",
                  fontWeight: isActive ? 600 : 300,
                  fontSize: "0.75rem",
                  letterSpacing: isActive ? "-0.01em" : "0.01em",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  boxShadow: isActive ? "0 0 10px rgba(255,255,255,0.15)" : "none",
                  outline: "none",
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(255,255,255,0.3)";
                    (e.currentTarget as HTMLButtonElement).style.color = "#fafafa";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(255,255,255,0.15)";
                    (e.currentTarget as HTMLButtonElement).style.color = "#a1a1a1";
                  }
                }}
              >
                <span style={{ fontSize: "0.9rem", opacity: isActive ? 1 : 0.7 }}>{cat.emoji}</span>
                <span>{cat.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ——— Score tiers ——— */}
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.625rem" }}>
          <span className="eyebrow-label">Prioritní skóre</span>
          <span
            style={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 300,
              fontSize: "0.6875rem",
              color: "#666666",
              letterSpacing: "0.02em",
            }}
          >
            1–10
          </span>
        </div>
        <div style={{ display: "flex", gap: "0.375rem" }} role="group" aria-label="Filtr prioritního skóre">
          {SCORE_TIERS.map((tier) => {
            const isActive = activeTierKey === tier.key;
            return (
              <button
                key={tier.key}
                onClick={() => selectTier(tier)}
                aria-pressed={isActive}
                style={{
                  flex: 1,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.375rem",
                  padding: "0.4375rem 0.5rem",
                  borderRadius: "20px",
                  border: isActive
                    ? "1px solid rgba(255,255,255,0.4)"
                    : "1px solid rgba(255,255,255,0.15)",
                  background: isActive ? "#ffffff" : "transparent",
                  color: isActive ? "#050505" : "#a1a1a1",
                  fontFamily: isActive ? "'Syne', sans-serif" : "'Inter', sans-serif",
                  fontWeight: isActive ? 600 : 300,
                  fontSize: "0.75rem",
                  letterSpacing: isActive ? "-0.01em" : "0.01em",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  boxShadow: isActive ? "0 0 10px rgba(255,255,255,0.15)" : "none",
                  outline: "none",
                  minHeight: "2rem",
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(255,255,255,0.3)";
                    (e.currentTarget as HTMLButtonElement).style.color = "#fafafa";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(255,255,255,0.15)";
                    (e.currentTarget as HTMLButtonElement).style.color = "#a1a1a1";
                  }
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    display: "inline-block",
                    width: "0.4375rem",
                    height: "0.4375rem",
                    borderRadius: "50%",
                    flexShrink: 0,
                    background: isActive ? "#050505" : tier.dotColor,
                    boxShadow: isActive
                      ? "none"
                      : tier.dotColor !== "#666666" && tier.dotColor !== "#a1a1a1"
                      ? `0 0 5px ${tier.dotColor}88`
                      : "none",
                    opacity: isActive ? 0.6 : 1,
                  }}
                />
                {tier.label}
              </button>
            );
          })}
        </div>

        {/* Segment bar */}
        <div
          aria-hidden="true"
          style={{
            marginTop: "0.625rem",
            display: "flex",
            gap: "2px",
            height: "3px",
            borderRadius: "4px",
            overflow: "hidden",
          }}
        >
          {Array.from({ length: 10 }, (_, i) => {
            const seg = i + 1;
            const inRange = seg >= value.minScore && seg <= value.maxScore;
            const color = activeTier ? activeTier.segmentColor : "rgba(255,255,255,0.1)";
            return (
              <div
                key={seg}
                style={{
                  flex: 1,
                  borderRadius: "2px",
                  transition: "background-color 0.2s ease",
                  backgroundColor: inRange ? color : "rgba(255,255,255,0.06)",
                  boxShadow: inRange && color !== "#a1a1a1" && color !== "rgba(255,255,255,0.1)"
                    ? `0 0 4px ${color}66`
                    : "none",
                }}
              />
            );
          })}
        </div>
      </div>

      {/* ——— Toggle show all ——— */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingTop: "0.75rem",
          borderTop: "1px solid rgba(255,255,255,0.07)",
        }}
      >
        <span
          style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 300,
            fontSize: "0.8125rem",
            color: "#a1a1a1",
            letterSpacing: "0.01em",
          }}
        >
          Zobrazit kino / divadlo / přednášky
        </span>
        <button
          onClick={toggleShowAll}
          role="switch"
          aria-checked={!value.hideDefaults}
          style={{
            position: "relative",
            display: "inline-flex",
            alignItems: "center",
            width: "2.25rem",
            height: "1.25rem",
            borderRadius: "999px",
            border: "1px solid rgba(255,255,255,0.15)",
            background: !value.hideDefaults
              ? "rgba(255,255,255,0.3)"
              : "rgba(255,255,255,0.1)",
            cursor: "pointer",
            transition: "background 0.2s ease, border-color 0.2s ease",
            padding: 0,
            flexShrink: 0,
            outline: "none",
          }}
        >
          <span
            style={{
              position: "absolute",
              width: "0.9rem",
              height: "0.9rem",
              borderRadius: "50%",
              background: "#ffffff",
              transform: !value.hideDefaults ? "translateX(1.15rem)" : "translateX(0.15rem)",
              transition: "transform 0.2s ease",
              boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
            }}
          />
        </button>
      </div>
    </div>
  );
};
